import { type Country, type ArticleData, type CountryEnumType } from '@sr/types';
import { getCountryEnumFromCountry } from '@sr/utils';

export type SubscriptionType = 'CO2' | 'CSRD_PREMIUM';

/** Swedish article data is the fallback for countries without their own subscription pricing. */
type ArticleDataByCountry = Partial<Record<CountryEnumType, ArticleData>> & { SE: ArticleData };

/** Feature flags that change article data shown in the frontend. */
export type ArticleDataFlags = {
  /**
   * The `csrd-persona` flag. On, CSRD premium is sold as CSRD Premium instead of CSRD Extended.
   * When the flag is removed, rename the CSRD_PREMIUM rows below and drop this override.
   */
  csrdPersona?: boolean;
};

const CSRD_PREMIUM_PERSONA_NAME = 'CSRD Premium';

const subscriptionArticleData: Record<SubscriptionType, ArticleDataByCountry> = {
  CO2: {
    SE: {
      name: 'CO₂ Impact',
      code: 4600,
      pricePerYear: 14900,
      pricePerMonth: 990,
      vat: 0.25,
      currency: 'SEK',
      trialLengthDays: 0,
      active: true,
    },
    FI: {
      name: 'CO₂ Impact',
      code: 4600,
      pricePerYear: 9000,
      pricePerMonth: 30,
      vat: 0.25,
      currency: 'EUR',
      trialLengthDays: 0,
      active: false,
    },
    DK: {
      name: 'CO₂ Impact, Abonnement',
      code: 890000,
      pricePerYear: 7900,
      pricePerMonth: 664,
      vat: 0.25,
      currency: 'DKK',
      trialLengthDays: 0,
      active: true,
    },
    NO: {
      name: 'CO₂ Impact, Abonnement',
      code: 4046,
      pricePerYear: 14900,
      pricePerMonth: 666,
      vat: 0.25,
      currency: 'NOK',
      trialLengthDays: 0,
      active: true,
    },
    PL: {
      name: 'CO₂ Impact, Abonnement',
      code: 123,
      pricePerYear: 14900,
      pricePerMonth: 666,
      vat: 0.25,
      currency: 'PLN',
      trialLengthDays: 0,
      active: false,
    },
  },
  CSRD_PREMIUM: {
    SE: {
      name: 'CSRD Extended',
      code: 9747,
      pricePerYear: 14900,
      pricePerMonth: 990,
      vat: 0.25,
      currency: 'SEK',
      trialLengthDays: 0,
      active: true,
    },
    FI: {
      name: 'CSRD Extended',
      code: 4600,
      pricePerYear: 9000,
      pricePerMonth: 30,
      vat: 0.25,
      currency: 'EUR',
      trialLengthDays: 0,
      active: false,
    },
    DK: {
      name: 'CSRD Extended',
      code: 860032,
      pricePerYear: 10000,
      pricePerMonth: 664,
      vat: 0.25,
      currency: 'DKK',
      trialLengthDays: 0,
      active: true,
    },
    NO: {
      name: 'CSRD Extended',
      code: 4045,
      pricePerYear: 14900,
      pricePerMonth: 666,
      vat: 0.25,
      currency: 'NOK',
      trialLengthDays: 0,
      active: true,
    },
  },
};

export const getArticleDataBySubscriptionType = (
  type: SubscriptionType,
  country: Country,
  { csrdPersona = false }: ArticleDataFlags = {},
): ArticleData => {
  const byCountry = subscriptionArticleData[type];
  const articleData = byCountry[getCountryEnumFromCountry(country)] ?? byCountry.SE;

  if (type === 'CSRD_PREMIUM' && csrdPersona) {
    return { ...articleData, name: CSRD_PREMIUM_PERSONA_NAME };
  }

  return articleData;
};

export const countryHasActiveSubscriptionType = (country: Country): boolean => {
  const co2ArticleData = getArticleDataBySubscriptionType('CO2', country);
  const csrdArticleData = getArticleDataBySubscriptionType('CSRD_PREMIUM', country);

  return co2ArticleData.active || csrdArticleData.active;
};

export const co2impactArticleDataByCountry = (country: Country): ArticleData => {
  return getArticleDataBySubscriptionType('CO2', country);
};

export const csrdPremiumArticleDataByCountry = (country: Country): ArticleData => {
  return getArticleDataBySubscriptionType('CSRD_PREMIUM', country);
};
